import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP } from '../utils/xp';
import { speakText, stopSpeaking } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadVocabularyPool, shuffle } from '../data/vocabPool';
import { getPracticeSentences } from '../data/practiceSentences';
import './Vocabulary.css';
import './Pronunciation.css';

const SESSION_SIZE = 8;
const MAX_RECORD_SECONDS = 10;

// A word the speech engine was not sure about counts as missed.
// Raise this number to be stricter, lower it to be more forgiving (0 to 1).
const CONFIDENCE_MIN = 0.8;

// Set to true only after adding api/generate-practice-sentences.js
const USE_AI_SENTENCES = false;

const TEXT = {
    en: {
        title: 'Pronunciation',
        loading: 'Loading your words...',
        menuTitle: 'Say it out loud',
        menuSub: 'Listen, record yourself, and see how clearly you were understood.',
        words: 'Words',
        wordsDesc: 'Single words from your lessons',
        phrases: 'Sentences',
        phrasesDesc: 'Short sentences made for your level',
        generating: 'Writing sentences for you...',
        mixed: 'Mixed',
        mixedDesc: 'A bit of everything',
        none: 'Nothing here yet',
        empty: 'There is nothing to practice yet. Finish a lesson first.',
        item: '{current} of {total}',
        listen: 'Listen',
        record: 'Tap to record',
        stop: 'Tap to stop',
        transcribing: 'Listening to you...',
        micError: 'The microphone is not available. Check that your browser has permission to use it.',
        heardNothing: 'I could not hear anything. Try again a little closer to the microphone.',
        failed: 'Something went wrong. Please try again.',
        heard: 'I heard',
        clear: 'Clear',
        almost: 'Almost there',
        retry: 'Try again',
        clarity: 'Clarity {percent}%',
        tryAgain: 'Try again',
        next: 'Next →',
        finish: 'See results →',
        done: 'Session complete',
        summary: '{clear} of {total} said clearly.',
        xp: '+{xp} XP',
        review: 'Worth another try',
        practiceAgain: 'Practice again',
        back: 'Back to home',
        note: 'The score shows how clearly the computer understood you. It does not judge your accent.',
    },
    it: {
        title: 'Pronuncia',
        loading: 'Caricamento delle tue parole...',
        menuTitle: 'Dillo ad alta voce',
        menuSub: 'Ascolta, registrati e scopri quanto sei stato comprensibile.',
        words: 'Parole',
        wordsDesc: 'Parole singole dalle tue lezioni',
        phrases: 'Frasi',
        phrasesDesc: 'Frasi brevi per il tuo livello',
        generating: 'Sto scrivendo delle frasi per te...',
        mixed: 'Miste',
        mixedDesc: 'Un po’ di tutto',
        none: 'Ancora niente',
        empty: 'Non c’è ancora niente da esercitare. Completa prima una lezione.',
        item: '{current} di {total}',
        listen: 'Ascolta',
        record: 'Tocca per registrare',
        stop: 'Tocca per fermare',
        transcribing: 'Ti sto ascoltando...',
        micError: 'Il microfono non è disponibile. Controlla che il browser abbia il permesso di usarlo.',
        heardNothing: 'Non ho sentito niente. Riprova più vicino al microfono.',
        failed: 'Qualcosa è andato storto. Riprova.',
        heard: 'Ho sentito',
        clear: 'Chiaro',
        almost: 'Quasi',
        retry: 'Riprova',
        clarity: 'Chiarezza {percent}%',
        tryAgain: 'Riprova',
        next: 'Avanti →',
        finish: 'Vedi i risultati →',
        done: 'Sessione completata',
        summary: '{clear} su {total} dette in modo chiaro.',
        xp: '+{xp} XP',
        review: 'Da riprovare',
        practiceAgain: 'Esercitati ancora',
        back: 'Torna alla home',
        note: 'Il punteggio mostra quanto il computer ti ha capito. Non giudica il tuo accento.',
    },
    es: {
        title: 'Pronunciación',
        loading: 'Cargando tus palabras...',
        menuTitle: 'Dilo en voz alta',
        menuSub: 'Escucha, grábate y comprueba con qué claridad te entendieron.',
        words: 'Palabras',
        wordsDesc: 'Palabras sueltas de tus lecciones',
        phrases: 'Frases',
        phrasesDesc: 'Oraciones cortas para tu nivel',
        generating: 'Escribiendo frases para ti...',
        mixed: 'Mixto',
        mixedDesc: 'Un poco de todo',
        none: 'Todavía nada',
        empty: 'Todavía no hay nada que practicar. Completa primero una lección.',
        item: '{current} de {total}',
        listen: 'Escuchar',
        record: 'Toca para grabar',
        stop: 'Toca para parar',
        transcribing: 'Te estoy escuchando...',
        micError: 'El micrófono no está disponible. Comprueba que el navegador tenga permiso para usarlo.',
        heardNothing: 'No oí nada. Inténtalo de nuevo más cerca del micrófono.',
        failed: 'Algo salió mal. Inténtalo de nuevo.',
        heard: 'Escuché',
        clear: 'Claro',
        almost: 'Casi',
        retry: 'Inténtalo otra vez',
        clarity: 'Claridad {percent}%',
        tryAgain: 'Intentar de nuevo',
        next: 'Siguiente →',
        finish: 'Ver resultados →',
        done: 'Sesión completada',
        summary: '{clear} de {total} dichas con claridad.',
        xp: '+{xp} XP',
        review: 'Vale la pena repetir',
        practiceAgain: 'Practicar otra vez',
        back: 'Volver al inicio',
        note: 'La puntuación muestra con qué claridad te entendió el ordenador. No juzga tu acento.',
    },
    fr: {
        title: 'Prononciation',
        loading: 'Chargement de tes mots...',
        menuTitle: 'Dis-le à voix haute',
        menuSub: 'Écoute, enregistre-toi et vois à quel point tu as été compris.',
        words: 'Mots',
        wordsDesc: 'Des mots seuls tirés de tes leçons',
        phrases: 'Phrases',
        phrasesDesc: 'Phrases courtes pour ton niveau',
        generating: 'J’écris des phrases pour toi...',
        mixed: 'Mixte',
        mixedDesc: 'Un peu de tout',
        none: 'Rien pour l’instant',
        empty: 'Il n’y a rien à pratiquer pour l’instant. Termine d’abord une leçon.',
        item: '{current} sur {total}',
        listen: 'Écouter',
        record: 'Touche pour enregistrer',
        stop: 'Touche pour arrêter',
        transcribing: 'Je t’écoute...',
        micError: 'Le micro n’est pas disponible. Vérifie que ton navigateur a la permission de l’utiliser.',
        heardNothing: 'Je n’ai rien entendu. Réessaie plus près du micro.',
        failed: 'Une erreur s’est produite. Réessaie.',
        heard: 'J’ai entendu',
        clear: 'Clair',
        almost: 'Presque',
        retry: 'Réessaie',
        clarity: 'Clarté {percent} %',
        tryAgain: 'Réessayer',
        next: 'Suivant →',
        finish: 'Voir les résultats →',
        done: 'Séance terminée',
        summary: '{clear} sur {total} dits clairement.',
        xp: '+{xp} XP',
        review: 'À retenter',
        practiceAgain: 'Pratiquer encore',
        back: 'Retour à l’accueil',
        note: 'Le score montre à quel point l’ordinateur t’a compris. Il ne juge pas ton accent.',
    },
    pt: {
        title: 'Pronúncia',
        loading: 'Carregando suas palavras...',
        menuTitle: 'Diga em voz alta',
        menuSub: 'Ouça, grave sua voz e veja com que clareza você foi entendido.',
        words: 'Palavras',
        wordsDesc: 'Palavras soltas das suas lições',
        phrases: 'Frases',
        phrasesDesc: 'Frases curtas para o seu nível',
        generating: 'Escrevendo frases para você...',
        mixed: 'Misto',
        mixedDesc: 'Um pouco de tudo',
        none: 'Ainda nada',
        empty: 'Ainda não há nada para praticar. Conclua primeiro uma lição.',
        item: '{current} de {total}',
        listen: 'Ouvir',
        record: 'Toque para gravar',
        stop: 'Toque para parar',
        transcribing: 'Estou ouvindo você...',
        micError: 'O microfone não está disponível. Verifique se o navegador tem permissão para usá-lo.',
        heardNothing: 'Não ouvi nada. Tente de novo mais perto do microfone.',
        failed: 'Algo deu errado. Tente novamente.',
        heard: 'Eu ouvi',
        clear: 'Claro',
        almost: 'Quase',
        retry: 'Tente de novo',
        clarity: 'Clareza {percent}%',
        tryAgain: 'Tentar novamente',
        next: 'Próxima →',
        finish: 'Ver resultados →',
        done: 'Sessão concluída',
        summary: '{clear} de {total} ditas com clareza.',
        xp: '+{xp} XP',
        review: 'Vale tentar de novo',
        practiceAgain: 'Praticar de novo',
        back: 'Voltar ao início',
        note: 'A pontuação mostra com que clareza o computador entendeu você. Ela não avalia o seu sotaque.',
    },
};

// ---------------------------------------------------------------------------
// Comparing what was said with what was expected
// ---------------------------------------------------------------------------
function normalize(text) {
    return (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['’`]/g, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function similarity(a, b) {
    if (a === b) return 1;
    if (!a.length || !b.length) return 0;
    const rows = a.length + 1;
    const cols = b.length + 1;
    const grid = Array.from({ length: rows }, (_, i) => [i, ...Array(cols - 1).fill(0)]);
    for (let j = 0; j < cols; j += 1) grid[0][j] = j;
    for (let i = 1; i < rows; i += 1) {
        for (let j = 1; j < cols; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            grid[i][j] = Math.min(grid[i - 1][j] + 1, grid[i][j - 1] + 1, grid[i - 1][j - 1] + cost);
        }
    }
    return 1 - grid[rows - 1][cols - 1] / Math.max(a.length, b.length);
}

function compareSpeech(expected, heardText, heardTokens) {
    const expectedWords = normalize(expected).split(' ').filter(Boolean);

    // Each heard word, with how sure the speech engine was about it
    let tokens = [];
    if (Array.isArray(heardTokens) && heardTokens.length > 0) {
        heardTokens.forEach(token => {
            const confidence = typeof token.confidence === 'number' ? token.confidence : 1;
            normalize(token.text).split(' ').filter(Boolean).forEach(word => tokens.push({ word, confidence }));
        });
    } else {
        tokens = normalize(heardText).split(' ').filter(Boolean).map(word => ({ word, confidence: 1 }));
    }
    const heardWords = tokens.map(token => token.word);

    // Elisions (anch'io / anche io, l'amico) sound the same but are written differently.
    // Only in that case, compare the two texts with the spaces removed.
    const hasElision = /['’]/.test(expected) || expectedWords.length !== heardWords.length;
    const sure = tokens.every(token => token.confidence >= CONFIDENCE_MIN);
    if (hasElision && sure && expectedWords.length > 0 && heardWords.length > 0) {
        const expectedJoined = expectedWords.join('');
        const heardJoined = heardWords.join('');
        const longest = Math.max(expectedJoined.length, heardJoined.length);
        const allowedEdits = 1 + Math.floor(longest / 14);
        const edits = Math.round((1 - similarity(expectedJoined, heardJoined)) * longest);
        if (edits <= allowedEdits) {
            return {
                marks: expectedWords.map(word => ({ word, ok: true })),
                percent: 100,
                verdict: 'clear',
            };
        }
    }

    const used = new Set();
    const marks = expectedWords.map(word => {
        // Short words must match exactly, longer words may differ by one letter
        const threshold = word.length <= 5 ? 1 : 0.8;
        let bestIndex = -1;
        let bestScore = 0;
        tokens.forEach((token, index) => {
            if (used.has(index)) return;
            if (token.confidence < CONFIDENCE_MIN) return;
            const score = similarity(word, token.word);
            if (score >= threshold && score > bestScore) {
                bestScore = score;
                bestIndex = index;
            }
        });
        if (bestIndex >= 0) used.add(bestIndex);
        return { word, ok: bestIndex >= 0 };
    });
    const matched = marks.filter(mark => mark.ok).length;
    const percent = expectedWords.length > 0 ? Math.round((matched / expectedWords.length) * 100) : 0;
    let verdict = 'retry';
    if (percent >= 85) verdict = 'clear';
    else if (percent >= 50) verdict = 'almost';
    return { marks, percent, verdict };
}

export default function Pronunciation() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage, activeLevel } = useUserLanguage();

    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key, values) => {
        let text = TEXT[uiLang][key] || TEXT.en[key];
        Object.keys(values || {}).forEach(name => {
            text = text.replace(`{${name}}`, values[name]);
        });
        return text;
    };

    const [loading, setLoading] = useState(true);
    const [pool, setPool] = useState([]);
    const [session, setSession] = useState([]);
    const [index, setIndex] = useState(0);
    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [checking, setChecking] = useState(false);
    const [attempt, setAttempt] = useState(null);
    const [errorKey, setErrorKey] = useState('');
    const [outcomes, setOutcomes] = useState({});
    const [finished, setFinished] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [earnedXp, setEarnedXp] = useState(0);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioRef = useRef(null);
    const xpGiven = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { navigate('/signin'); return; }
            const items = await loadVocabularyPool(user.id, targetLang, uiLang);
            if (cancelled) return;
            setPool(items);
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [targetLang, uiLang, navigate]);

    useEffect(() => () => {
        try { recorderRef.current?.stop(); } catch (e) { }
        streamRef.current?.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);
        audioRef.current?.pause();
        stopSpeaking();
    }, []);

    const wordItems = pool.filter(item => item.type === 'word');
    const phraseItems = pool.filter(item => item.type === 'phrase');

    const beginWith = (list) => {
        setSession(list);
        setIndex(0);
        setAttempt(null);
        setErrorKey('');
        setOutcomes({});
        setFinished(false);
        setEarnedXp(0);
        xpGiven.current = false;
    };

    // Real sentences, written for the learner's level and built around words they know
    const makeSentences = async () => {
        const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
        if (!USE_AI_SENTENCES) return getPracticeSentences(targetLang, uiLang, level, SESSION_SIZE);
        const lessonWords = wordItems.filter(item => item.source !== 'starter');
        const base = lessonWords.length >= SESSION_SIZE ? lessonWords : wordItems;
        const chosen = shuffle(base).slice(0, SESSION_SIZE).map(item => item.word);
        try {
            const response = await fetch('/api/generate-practice-sentences', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ language: targetLang, level, interfaceLanguage: uiLang, words: chosen, count: SESSION_SIZE }),
            });
            if (!response.ok) throw new Error('failed');
            const data = await response.json();
            const list = (data.sentences || [])
                .filter(entry => entry && entry.text)
                .map(entry => ({
                    word: entry.text,
                    translation: entry.translation || '',
                    phonetic: '',
                    note: '',
                    emoji: '',
                    type: 'phrase',
                    source: 'generated',
                    topicSlug: '',
                }));
            if (list.length > 0) return list;
        } catch (err) {
            // Fall through to the saved phrases below
        }
        return shuffle(phraseItems).slice(0, SESSION_SIZE);
    };

    const startSession = async (kind) => {
        if (kind === 'words') {
            beginWith(shuffle(wordItems).slice(0, SESSION_SIZE));
        } else if (kind === 'phrases') {
            setGenerating(true);
            const list = await makeSentences();
            setGenerating(false);
            if (list.length > 0) beginWith(list);
        } else {
            beginWith(shuffle(pool).slice(0, SESSION_SIZE));
        }
    };

    const speak = async (text) => {
        try {
            audioRef.current?.pause();
            stopSpeaking();
            const response = await fetch('/api/tts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, language: targetLang }),
            });
            if (!response.ok) throw new Error('tts failed');
            const { audio } = await response.json();
            const player = new Audio(`data:audio/mpeg;base64,${audio}`);
            audioRef.current = player;
            player.play().catch(() => { });
        } catch (err) {
            speakText(text, undefined, targetLang);
        }
    };

    const stopRecording = () => {
        clearInterval(timerRef.current);
        setRecording(false);
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === 'inactive') return;
        recorder.onstop = () => {
            streamRef.current?.getTracks().forEach(track => track.stop());
            const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
            checkRecording(blob);
        };
        recorder.stop();
    };

    const startRecording = async () => {
        setErrorKey('');
        setAttempt(null);
        audioRef.current?.pause();
        stopSpeaking();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            chunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };
            recorderRef.current = recorder;
            recorder.start();
            setRecording(true);
            setSeconds(0);
            timerRef.current = setInterval(() => {
                setSeconds(value => {
                    if (value + 1 >= MAX_RECORD_SECONDS) {
                        stopRecording();
                    }
                    return value + 1;
                });
            }, 1000);
        } catch (err) {
            setErrorKey('micError');
        }
    };

    const checkRecording = async (blob) => {
        if (!blob || blob.size < 1000) {
            setErrorKey('heardNothing');
            return;
        }
        setChecking(true);
        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            const createRes = await fetch('/api/transcribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audio: base64, language: targetLang }),
            });
            if (!createRes.ok) throw new Error('create failed');
            const { id } = await createRes.json();

            let text = '';
            let words = [];
            for (let i = 0; i < 25; i += 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const pollRes = await fetch(`/api/transcribe?id=${id}`);
                const poll = await pollRes.json();
                if (poll.status === 'completed') { text = poll.text || ''; words = poll.words || []; break; }
                if (poll.status === 'error') break;
            }

            if (!text.trim()) {
                setErrorKey('heardNothing');
            } else {
                const target = session[index];
                const result = compareSpeech(target.word, text, words);
                setAttempt({ heard: text, ...result });
                // Keep the best result for each item
                setOutcomes(previous => {
                    const old = previous[target.word];
                    if (old && old.percent >= result.percent) return previous;
                    return { ...previous, [target.word]: { percent: result.percent, verdict: result.verdict, item: target } };
                });
            }
        } catch (err) {
            setErrorKey('failed');
        }
        setChecking(false);
    };

    const goNext = () => {
        if (index + 1 >= session.length) {
            const clearCount = Object.values(outcomes).filter(outcome => outcome.verdict === 'clear').length;
            if (!xpGiven.current) {
                xpGiven.current = true;
                const xp = Math.min(20, clearCount * 3);
                setEarnedXp(xp);
                if (xp > 0) awardXP(xp, 'pronunciation');
            }
            setFinished(true);
        } else {
            setIndex(index + 1);
            setAttempt(null);
            setErrorKey('');
        }
    };

    const backToMenu = () => {
        audioRef.current?.pause();
        stopSpeaking();
        setSession([]);
        setFinished(false);
    };

    const topBar = (title, onClose, right) => (
        <div className="vb-top-bar">
            <button className="vb-close" onClick={onClose}>✕</button>
            <div className="vb-title">{title}</div>
            {right || <div style={{ width: 32 }}></div>}
        </div>
    );

    if (loading) {
        return (
            <div className="vb-screen">
                {topBar(s('title'), () => navigate('/home'))}
                <div className="vb-state">{s('loading')}</div>
            </div>
        );
    }

    if (generating) {
        return (
            <div className="vb-screen">
                {topBar(s('title'), () => setGenerating(false))}
                <div className="vb-state">{s('generating')}</div>
            </div>
        );
    }

    // ---------- Menu ----------
    if (session.length === 0) {
        const options = [
            { kind: 'words', emoji: '🔤', name: s('words'), desc: s('wordsDesc'), count: wordItems.length },
            { kind: 'phrases', emoji: '💬', name: s('phrases'), desc: s('phrasesDesc'), count: pool.length },
            { kind: 'mixed', emoji: '🎲', name: s('mixed'), desc: s('mixedDesc'), count: pool.length },
        ];
        return (
            <div className="vb-screen">
                {topBar(s('title'), () => navigate('/home'))}
                <div className="vb-menu">
                    <div className="vb-menu-icon">🎙️</div>
                    {pool.length === 0 ? (
                        <div className="vb-state">{s('empty')}</div>
                    ) : (
                        <>
                            <div className="vb-menu-title">{s('menuTitle')}</div>
                            <div className="vb-menu-sub">{s('menuSub')}</div>
                            {options.map(option => (
                                <button
                                    key={option.kind}
                                    className="vb-mode-card"
                                    disabled={option.count === 0}
                                    onClick={() => startSession(option.kind)}
                                >
                                    <div className="vb-mode-emoji">{option.emoji}</div>
                                    <div>
                                        <div className="vb-mode-name">{option.name}</div>
                                        <div className="vb-mode-desc">{option.count === 0 ? s('none') : option.desc}</div>
                                    </div>
                                </button>
                            ))}
                            <div className="pr-note">{s('note')}</div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---------- Results ----------
    if (finished) {
        const all = Object.values(outcomes);
        const clearCount = all.filter(outcome => outcome.verdict === 'clear').length;
        const needWork = all.filter(outcome => outcome.verdict !== 'clear');
        return (
            <div className="vb-screen">
                {topBar(s('title'), backToMenu)}
                <div className="vb-results">
                    <div className="vb-results-icon">🎉</div>
                    <div className="vb-results-title">{s('done')}</div>
                    <div className="vb-results-score">{s('summary', { clear: clearCount, total: session.length })}</div>
                    {earnedXp > 0 && <div className="vb-results-xp">{s('xp', { xp: earnedXp })}</div>}

                    {needWork.length > 0 && (
                        <div className="vb-review">
                            <div className="vb-review-label">{s('review')}</div>
                            {needWork.map(outcome => (
                                <div key={outcome.item.word} className="vb-review-row">
                                    <div>
                                        <div className="vb-review-word">{outcome.item.word}</div>
                                        {outcome.item.translation && <div className="vb-review-meaning">{outcome.item.translation}</div>}
                                    </div>
                                    <button className="vb-listen-btn" onClick={() => speak(outcome.item.word)} aria-label={s('listen')}>🔊</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button className="vb-primary-btn" onClick={backToMenu}>{s('practiceAgain')}</button>
                    <button className="vb-secondary-btn" onClick={() => navigate('/home')}>{s('back')}</button>
                </div>
            </div>
        );
    }

    // ---------- Practice ----------
    const item = session[index];
    const counter = <div className="vb-counter">{s('item', { current: index + 1, total: session.length })}</div>;

    return (
        <div className="vb-screen">
            {topBar(s('title'), backToMenu, counter)}
            <div className="vb-content">
                <div className="vb-card pr-card">
                    {item.emoji && <div className="vb-card-emoji">{item.emoji}</div>}
                    <div className="vb-card-word">{item.word}</div>
                    {item.phonetic && <div className="vb-card-phonetic">{item.phonetic}</div>}
                    {item.translation && <div className="vb-card-meaning pr-meaning">{item.translation}</div>}
                </div>

                <button className="vb-listen-wide" onClick={() => speak(item.word)}>🔊 {s('listen')}</button>

                {!checking && (
                    <button
                        className={`pr-mic-btn ${recording ? 'recording' : ''}`}
                        onClick={recording ? stopRecording : startRecording}
                    >
                        {recording ? `⏹ ${s('stop')} (${seconds}s)` : `🎙️ ${attempt ? s('tryAgain') : s('record')}`}
                    </button>
                )}

                {checking && <div className="pr-status">{s('transcribing')}</div>}
                {errorKey && <div className="pr-error">{s(errorKey)}</div>}

                {attempt && !checking && (
                    <div className={`pr-result ${attempt.verdict}`}>
                        <div className="pr-verdict">
                            {s(attempt.verdict)} · {s('clarity', { percent: attempt.percent })}
                        </div>
                        <div className="pr-chips">
                            {attempt.marks.map((mark, i) => (
                                <span key={i} className={`pr-chip ${mark.ok ? 'ok' : 'miss'}`}>{mark.word}</span>
                            ))}
                        </div>
                        <div className="pr-heard">{s('heard')}: “{attempt.heard}”</div>
                    </div>
                )}

                {attempt && !checking && (
                    <button className="vb-primary-btn" onClick={goNext}>
                        {index + 1 >= session.length ? s('finish') : s('next')}
                    </button>
                )}
            </div>
        </div>
    );
}